import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload, X, Save, Wand2 } from 'lucide-react';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';

const PropertyForm = () => {
    const { id } = useParams(); // If ID exists, we are editing
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [citiesList, setCitiesList] = useState([]);

    const [formData, setFormData] = useState({
        title: '',
        catch_phrase: '',
        price: '',
        surface: '',
        rooms: '',
        city: '',
        type: 'Maison',
        status: 'Vente',
        marketing_status: 'Disponible',
        description: '',
        image_url: '',
        images: [],
        features: '',
        dpe_energy: '',
        dpe_ges: '',
        is_favorite: false,
        is_sync_locked: false,
        netty_ref: null
    });

    useEffect(() => {
        const fetchCities = async () => {
            const [localCities, propertiesCities] = await Promise.all([
                supabase.from('cities').select('*').order('name', { ascending: true }),
                supabase.from('properties').select('city').not('city', 'is', 'null')
            ]);
            
            let combined = [];
            if (localCities.data) combined = [...localCities.data];
            
            if (propertiesCities.data) {
                const existingNames = new Set(combined.map(c => c.name.toLowerCase().trim()));
                propertiesCities.data.forEach(p => {
                    const cityName = p.city ? p.city.trim() : '';
                    if (cityName && !existingNames.has(cityName.toLowerCase())) {
                        existingNames.add(cityName.toLowerCase());
                        combined.push({ id: `modelo-${cityName}`, name: cityName, sector: 'Autre' });
                    }
                });
            }
            
            combined.sort((a, b) => a.name.localeCompare(b.name));
            setCitiesList(combined);
        };
        fetchCities();
    }, []);

    useEffect(() => {
        if (isEditMode) {
            const fetchProperty = async () => {
                const { data, error } = await supabase
                    .from('properties')
                    .select('*')
                    .eq('id', id)
                    .single();
                if (data) {
                    setFormData({
                        ...data,
                        features: data.features ? data.features.join(', ') : ''
                    });
                }
            };
            fetchProperty();
        }
    }, [isEditMode, id]);

    // IMAGE COMPRESSION HELPER
    const compressImage = (file, maxWidth = 2000) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    let width = img.width;
                    let height = img.height;
                    if (width > maxWidth) {
                        height = (maxWidth / width) * height;
                        width = maxWidth;
                    } else {
                        return resolve(file);
                    }
                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                    canvas.toBlob((blob) => {
                        const compressedFile = new File([blob], file.name, {
                            type: 'image/jpeg',
                            lastModified: Date.now(),
                        });
                        resolve(compressedFile);
                    }, 'image/jpeg', 0.85);
                };
            };
        });
    };

    const onDrop = useCallback(async (acceptedFiles) => {
        const file = acceptedFiles[0];
        if (!file) return;

        setUploading(true);
        try {
            const optimizedFile = await compressImage(file);
            const fileExt = file.name.split('.').pop();
            const fileName = `${Math.random()}.${fileExt}`;
            const filePath = `${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('property-images')
                .upload(filePath, optimizedFile);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('property-images')
                .getPublicUrl(filePath);

            setFormData(prev => ({ ...prev, image_url: publicUrl }));
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Erreur lors du téléchargement de l\'image.');
        } finally {
            setUploading(false);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'image/*': [] },
        maxFiles: 1
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const payload = {
            ...formData,
            price: parseFloat(formData.price) || 0,
            surface: parseFloat(formData.surface) || 0,
            rooms: parseInt(formData.rooms) || 0,
            images: formData.images || [],
            marketing_status: formData.marketing_status || 'Disponible',
            features: formData.features.split(',').map(f => f.trim()).filter(f => f !== '')
        };

        try {
            // Vérifier si la ville existe déjà
            if (payload.city) {
                const cityName = payload.city.trim();
                const exists = citiesList.some(c => c.name.toLowerCase() === cityName.toLowerCase());
                if (!exists) {
                    await supabase
                        .from('cities')
                        .insert([{ name: cityName, sector: 'Ottange' }]);
                }
            }

            if (isEditMode) {
                const { error } = await supabase
                    .from('properties')
                    .update(payload)
                    .eq('id', id);
                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('properties')
                    .insert([payload]);
                if (error) throw error;
            }
            navigate('/admin');
        } catch (error) {
            console.error('Error saving property:', error);
            alert(`Erreur lors de la sauvegarde : ${error.message || error.details || JSON.stringify(error)}`);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    return (
        <div className="max-w-4xl mx-auto py-12 px-4 font-sans">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-black text-[#2C353D]">
                    {isEditMode ? 'Détails de l\'Annonce' : 'Nouvelle Annonce MODELO'}
                </h1>
                <button onClick={() => navigate('/admin')} className="text-gray-500 hover:text-[#EA723D] font-bold">Retour</button>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 space-y-8">

                {/* Sync Logic Warning */}
                {formData.netty_ref && (
                    <div className={`p-6 rounded-2xl border transition-all ${formData.is_sync_locked ? 'bg-orange-50 border-orange-200 shadow-sm shadow-orange-100' : 'bg-blue-50 border-blue-100 shadow-sm shadow-blue-100'}`}>
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <h3 className={`text-sm font-black uppercase tracking-widest mb-2 flex items-center gap-2 ${formData.is_sync_locked ? 'text-orange-700' : 'text-blue-700'}`}>
                                    {formData.is_sync_locked ? '🔒 Synchronisation MODELO Verrouillée' : '🔄 Synchronisation MODELO Active'}
                                </h3>
                                <p className="text-xs leading-relaxed text-gray-600 font-medium">
                                    {formData.is_sync_locked
                                        ? "Vous pouvez modifier le titre, la description et les photos librement. Ces champs ne seront plus écrasés par MODELO. Seuls le prix et le statut restent synchronisés."
                                        : "Toute modification faite ici sur le Titre, la Description ou les Photos sera ÉCRASÉE lors de la prochaine synchronisation automatique avec MODELO."}
                                </p>
                            </div>
                            <label className="flex items-center gap-3 cursor-pointer group ml-4">
                                <div className={`relative w-14 h-7 rounded-full transition-all duration-300 ${formData.is_sync_locked ? 'bg-orange-500 ring-4 ring-orange-100' : 'bg-blue-400 ring-4 ring-blue-50'}`}>
                                    <div className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-300 ${formData.is_sync_locked ? 'translate-x-7' : 'translate-x-0'}`}></div>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={formData.is_sync_locked}
                                    onChange={(e) => {
                                        const newVal = e.target.checked;
                                        if (!newVal) {
                                            if (!window.confirm("⚠️ ATTENTION : Réactiver la synchro va écraser vos modifications manuelles. Continuer ?")) return;
                                        }
                                        setFormData({ ...formData, is_sync_locked: newVal });
                                    }}
                                    className="hidden"
                                />
                            </label>
                        </div>
                    </div>
                )}

                {/* Image Upload */}
                <div>
                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2 uppercase text-xs tracking-wide">Photo principale</label>
                            <div {...getRootProps()} className={`border-2 border-dashed rounded-xl h-64 flex flex-col items-center justify-center cursor-pointer transition-colors relative overflow-hidden ${isDragActive ? 'border-[#EA723D] bg-orange-50' : 'border-gray-300 hover:border-[#2C353D]'}`}>
                                <input {...getInputProps()} />
                                {formData.image_url ? (
                                    <div className="relative w-full h-full group">
                                        <img src={formData.image_url} alt="Aperçu" className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white font-bold">
                                            Changer
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center text-gray-400 p-4">
                                        {uploading ? <p>Chargement...</p> : <><Upload size={32} className="mx-auto mb-2" /><p className="text-xs font-bold">Importer la Photo</p></>}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2 uppercase text-xs tracking-wide">Galerie Photos</label>
                            <div className="border-2 border-dashed border-gray-300 rounded-xl min-h-[16rem] p-4 bg-gray-50/30">
                                <div className="grid grid-cols-3 gap-2 mb-4">
                                    {formData.images && formData.images.map((img, idx) => (
                                        <div key={idx} className="relative aspect-square rounded-lg overflow-hidden group border border-gray-100">
                                            <img src={img} alt="" className="w-full h-full object-cover" />
                                            <button
                                                type="button"
                                                onClick={() => setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }))}
                                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ))}
                                    <label className="border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#EA723D] hover:text-[#EA723D] text-gray-400 transition-colors aspect-square bg-white">
                                        <input
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            className="hidden"
                                            onChange={async (e) => {
                                                const files = Array.from(e.target.files);
                                                setUploading(true);
                                                for (const file of files) {
                                                    const optimizedFile = await compressImage(file);
                                                    const fileExt = file.name.split('.').pop();
                                                    const fileName = `gallery-${Math.random()}.${fileExt}`;
                                                    const { error } = await supabase.storage.from('property-images').upload(fileName, optimizedFile);
                                                    if (!error) {
                                                        const { data: { publicUrl } } = supabase.storage.from('property-images').getPublicUrl(fileName);
                                                        setFormData(prev => ({ ...prev, images: [...(prev.images || []), publicUrl] }));
                                                    }
                                                }
                                                setUploading(false);
                                            }}
                                        />
                                        <Upload size={20} />
                                        <span className="text-[10px] font-bold mt-1">Ajouter</span>
                                    </label>
                                </div>
                                <p className="text-center text-[10px] text-gray-400 font-bold uppercase tracking-tighter">Cliquez sur le + pour ajouter des photos</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Titre de l'annonce</label>
                        <input required name="title" value={formData.title} onChange={handleChange} className="w-full p-4 border border-gray-200 rounded-xl outline-none focus:border-[#EA723D] font-bold text-[#2C353D] bg-gray-50/30" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Phrase d'accroche</label>
                        <input name="catch_phrase" value={formData.catch_phrase} onChange={handleChange} className="w-full p-4 border border-gray-200 rounded-xl outline-none focus:border-[#EA723D] bg-gray-50/30" placeholder="Optionnel" />
                    </div>
                </div>

                <div className="grid md:grid-cols-4 gap-6">
                    <div className="space-y-2">
                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Prix (€)</label>
                        <input required type="number" name="price" value={formData.price} onChange={handleChange} className="w-full p-4 border border-gray-200 rounded-xl outline-none focus:border-[#EA723D] bg-gray-50/30" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Surface (m²)</label>
                        <input required type="number" name="surface" value={formData.surface} onChange={handleChange} className="w-full p-4 border border-gray-200 rounded-xl outline-none focus:border-[#EA723D] bg-gray-50/30" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Chambres</label>
                        <input required type="number" name="rooms" value={formData.rooms || ''} onChange={handleChange} className="w-full p-4 border border-gray-200 rounded-xl outline-none focus:border-[#EA723D] bg-gray-50/30" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Ville</label>
                        {formData.netty_ref ? (
                            <input disabled value={formData.city || ''} className="w-full p-4 border border-gray-100 rounded-xl outline-none bg-gray-100 text-gray-400 font-bold cursor-not-allowed" title="Ce champ est automatiquement géré par Modelo" />
                        ) : (
                            <select required name="city" value={formData.city} onChange={handleChange} className="w-full p-4 border border-gray-200 rounded-xl outline-none focus:border-[#EA723D] bg-white font-bold text-[#2C353D]">
                                <option value="">Choisir...</option>
                                {citiesList.map(city => (
                                    <option key={city.id} value={city.name}>{city.name}</option>
                                ))}
                                {formData.city && !citiesList.some(c => c.name === formData.city) && (
                                    <option value={formData.city}>{formData.city}</option>
                                )}
                            </select>
                        )}
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Type de bien</label>
                        <select name="type" value={formData.type} onChange={handleChange} className="w-full p-4 border border-gray-200 rounded-xl outline-none focus:border-[#EA723D] bg-white font-bold">
                            <option>Maison</option>
                            <option>Appartement</option>
                            <option>Terrain</option>
                            <option>Immeuble</option>
                            <option>Local Commercial</option>
                        </select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Type d'offre</label>
                        <select name="status" value={formData.status} onChange={handleChange} className="w-full p-4 border border-gray-200 rounded-xl outline-none focus:border-[#EA723D] bg-white font-bold">
                            <option value="Vente">Vente</option>
                            <option value="Location">Location</option>
                        </select>
                    </div>
                </div>

                {/* Promotion Section */}
                <div className="grid md:grid-cols-2 gap-6 items-start">
                    <div className="flex items-center gap-4 bg-gray-50 p-6 rounded-2xl border border-gray-100">
                        <label className="flex items-center gap-4 cursor-pointer group">
                            <div className={`relative w-12 h-6 rounded-full p-1 transition-all duration-300 ${formData.is_favorite ? 'bg-red-500' : 'bg-gray-300'}`}>
                                <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-300 ${formData.is_favorite ? 'translate-x-6' : 'translate-x-0'}`}></div>
                            </div>
                            <input
                                type="checkbox"
                                name="is_favorite"
                                checked={formData.is_favorite}
                                onChange={(e) => setFormData({ ...formData, is_favorite: e.target.checked })}
                                className="hidden"
                            />
                            <span className="font-black text-[#2C353D] uppercase text-xs tracking-widest flex items-center gap-2">
                                ❤️ Coup de Cœur Ottange
                            </span>
                        </label>
                    </div>

                    <div className="space-y-2 p-6 bg-gray-50 rounded-2xl border border-gray-100">
                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Bandeau Statut (Badge)</label>
                        <select
                            name="marketing_status"
                            value={formData.marketing_status || 'Disponible'}
                            onChange={handleChange}
                            className="w-full p-4 border border-gray-200 rounded-xl outline-none focus:border-[#EA723D] bg-white font-bold text-[#EA723D]"
                        >
                            <option value="Disponible">Aucun (Disponible)</option>
                            <option value="Nouveauté">✨ Nouveauté</option>
                            <option value="Exclusivité">⭐️ Exclusivité</option>
                            <option value="Sous Offre">🤝 Sous Offre</option>
                            <option value="Sous Compromis">📝 Sous Compromis</option>
                            <option value="Vendu">🥂 Vendu</option>
                            <option value="Baisse de prix">📉 Baisse de prix</option>
                            <option value="Loué">🤝 Loué</option>
                        </select>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest">DPE (Énergie)</label>
                        <select name="dpe_energy" value={formData.dpe_energy} onChange={handleChange} className="w-full p-4 border border-gray-200 rounded-xl outline-none focus:border-[#EA723D] bg-white font-bold">
                            <option value="">Non renseigné</option>
                            {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(l => (
                                <option key={l} value={l}>Classe {l}</option>
                            ))}
                        </select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest">GES (Climat)</label>
                        <select name="dpe_ges" value={formData.dpe_ges} onChange={handleChange} className="w-full p-4 border border-gray-200 rounded-xl outline-none focus:border-[#EA723D] bg-white font-bold">
                            <option value="">Non renseigné</option>
                            {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(l => (
                                <option key={l} value={l}>Classe {l}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="flex justify-between items-end">
                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Description de l'offre</label>
                        <button
                            type="button"
                            onClick={() => {
                                const desc = formData.description;
                                const descLower = desc.toLowerCase();
                                const newUpdates = {};
                                let detectedFeatures = [...(formData.features ? formData.features.split(',').map(f => f.trim()).filter(f => f) : [])];
                                const surfaceMatch = descLower.match(/(\d+(?:[.,]\d+)?)\s*m[2²]/);
                                if (surfaceMatch && !formData.surface) newUpdates.surface = surfaceMatch[1].replace(',', '.');
                                const roomMatches = [...descLower.matchAll(/(\d+)\s*chambre/g)];
                                let totalRooms = 0; roomMatches.forEach(m => totalRooms += parseInt(m[1]));
                                if (totalRooms === 0 && descLower.includes('chambre')) totalRooms = 1;
                                if (totalRooms > 0 && !formData.rooms) newUpdates.rooms = totalRooms;
                                const priceMatch = desc.match(/(\d[\d\s]*)(?:€|euros)/i);
                                if (priceMatch && !formData.price) newUpdates.price = priceMatch[1].replace(/\s/g, '');
                                if (descLower.includes('appartement') || descLower.includes('studio')) newUpdates.type = 'Appartement';
                                else if (descLower.includes('terrain')) newUpdates.type = 'Terrain';
                                else if (descLower.includes('immeuble')) newUpdates.type = 'Immeuble';
                                else if (descLower.includes('local commercial')) newUpdates.type = 'Local Commercial';
                                else if (descLower.includes('maison')) newUpdates.type = 'Maison';
                                const localCities = citiesList.length > 0 ? citiesList.map(c => c.name) : [
                                    'Ottange', 'Audun-le-Tiche', 'Russange', 'Villerupt', 'Aumetz', 'Boulange', 'Beuvillers', 'Serrouville', 'Tressange', 'Fontoy', 'Algrange', 'Nilvange', 'Knutange', 'Hayange'
                                ];
                                let bestCity = localCities.find(city => descLower.includes(city.toLowerCase()));
                                if (bestCity && !formData.city) newUpdates.city = bestCity;


                                setFormData(prev => ({ ...prev, ...newUpdates, features: detectedFeatures.join(', ') }));
                                alert("🪄 Analyse terminée (Champs détectés mis à jour)");
                            }}
                            className="text-[10px] font-black text-[#EA723D] flex items-center gap-1 hover:text-[#2C353D] transition-colors uppercase tracking-widest"
                        >
                            <Wand2 size={14} /> Analyse Magique
                        </button>
                    </div>
                    <textarea required name="description" value={formData.description} onChange={handleChange} rows="10" className="w-full p-4 border border-gray-200 rounded-xl outline-none focus:border-[#EA723D] bg-gray-50/30" placeholder="Le contenu de l'annonce..."></textarea>
                </div>

                <div className="space-y-4">
                    <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Atouts du bien</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            { key: 'Ascenseur',             icon: '🛗' },
                            { key: 'Balcon',                icon: '🌅' },
                            { key: 'Buanderie',             icon: '🫧' },
                            { key: 'Cave',                  icon: '🪣' },
                            { key: 'Climatisation',         icon: '❄️' },
                            { key: 'Combles aménageables',  icon: '🏠' },
                            { key: 'Cour extérieure',       icon: '🌿' },
                            { key: 'Garage',                icon: '🚗' },
                            { key: 'Grenier',               icon: '📦' },
                            { key: 'Jardin',                icon: '🌳' },
                            { key: 'Parking',               icon: '🅿️' },
                            { key: 'Piscine',               icon: '🏊' },
                            { key: 'Sous-sol',              icon: '⬇️' },
                            { key: 'Terrasse',              icon: '☀️' },
                            { key: 'Véranda',               icon: '🪟' },
                            { key: 'Rénové',                icon: '✨' },
                            { key: 'Sans travaux',          icon: '👌' },
                        ].map(({ key: feat, icon }) => {
                            const isChecked = formData.features.includes(feat);
                            return (
                                <label key={feat} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${isChecked ? 'bg-[#EA723D]/5 border-[#EA723D]' : 'bg-white border-gray-100 hover:border-gray-200 shadow-sm'}`}>
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => {
                                            const currentFeatures = formData.features ? formData.features.split(',').map(f => f.trim()).filter(f => f) : [];
                                            let newFeatures = e.target.checked ? [...currentFeatures, feat] : currentFeatures.filter(f => f !== feat);
                                            setFormData({ ...formData, features: newFeatures.join(', ') });
                                        }}
                                        className="w-4 h-4 accent-[#EA723D]"
                                    />
                                    <span className="text-base">{icon}</span>
                                    <span className={`text-[10px] font-black uppercase tracking-tighter leading-tight ${isChecked ? 'text-[#EA723D]' : 'text-gray-400'}`}>{feat}</span>
                                </label>
                            );
                        })}
                    </div>
                    <div className="mt-4">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Autres caractéristiques (Séparez par virgules)</label>
                        <input name="features" value={formData.features} onChange={handleChange} className="w-full p-4 mt-2 border border-gray-200 rounded-xl outline-none focus:border-[#EA723D] bg-gray-50/30 font-bold text-xs" placeholder="Ex: Cheminée, Cuisine équipée..." />
                    </div>
                </div>

                <div className="pt-8 border-t border-gray-100 flex justify-end">
                    <Button type="submit" disabled={loading} className="px-10 py-5 bg-[#2C353D] hover:bg-[#1a1f24] text-white rounded-2xl shadow-xl shadow-gray-200 transition-all">
                        {loading ? 'Enregistrement...' : <><Save size={20} /> Enregistrer les modifications</>}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default PropertyForm;
